import * as React from 'react';
import { ArrowBendUpLeft } from '@phosphor-icons/react';

import type { SidebarNavItem } from '../types';
import { useSidebar } from '../context';
import { SectionNav } from '../section-nav';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface AppNavProps {
  className?: string;
  items?: SidebarNavItem[];
  activeId?: string;
  showBack?: boolean;
  onBack?: () => void;
  sectionNavItems?: SidebarNavItem[];
  sectionNavActiveId?: string;
}

export function AppNav({
  className,
  items,
  activeId,
  showBack,
  onBack,
  sectionNavItems,
  sectionNavActiveId,
}: AppNavProps) {
  const { collapsed, ItemLinkComponent } = useSidebar();

  const hasBack = !!showBack && !!onBack;

  return (
    <nav className={cn('flex flex-col gap-2', className)} aria-label="App navigation">
      {hasBack && (
        <Button
          variant="link"
          className={cn('py-2 text-base text-default w-full mx-4', collapsed ? 'justify-center' : 'justify-start')}
          size="icon"
          onClick={onBack}
        >
          <ArrowBendUpLeft aria-hidden="true" />
          <span className={cn(collapsed && 'sr-only')}>back to Brainfish</span>
        </Button>
      )}
      <ScrollArea className="min-h-0 max-h-[calc(100dvh-var(--header-nav-height)-5rem)]">
        <div className="flex flex-col gap-2 mx-4">
          {items?.map((item) => (
            <React.Fragment key={item.id}>
              <ItemLinkComponent
                className={cn(
                  'flex p-2 rounded-lg gap-1 text-base text-default items-center',
                  item.id === activeId
                    ? 'bg-primary text-primary-foreground font-bold hover:text-primary-foreground focus:text-primary-foreground'
                    : 'hover:bg-dark-200 focus:bg-dark-200 hover:text-accent-foreground focus:text-accent-foreground',
                )}
                href={item.href}
                data-sidebar-item-id={item.id}
                data-sidebar-item-type="app"
                data-sidebar-app-nav-item-selected={item.id === activeId}
              >
                {item.Icon ? <item.Icon weight={item.id === activeId ? 'fill' : 'regular'} size={16} /> : null}
                <span className={cn('transition-opacity duration-200', collapsed && 'opacity-0 sr-only')}>
                  {item.label}
                </span>
              </ItemLinkComponent>
              {item.id === activeId && sectionNavItems && sectionNavItems.length > 0 && !collapsed && (
                <SectionNav className={cn('flex-shrink-0')} items={sectionNavItems} activeId={sectionNavActiveId} />
              )}
            </React.Fragment>
          ))}
        </div>
      </ScrollArea>
    </nav>
  );
}
